// components/AutomatedTradingDisclaimerModal.jsx
import React, { useState } from 'react';
import { TRADING_WARNINGS, DISCLOSURE_VERSION } from '../../../config/riskDisclosure';
import useStockStore from '../../../stores/useStockStore';
import { addDoc, collection, serverTimestamp, setDoc, doc } from 'firebase/firestore';
import { auth, db } from '../../../services/firebase';
import { useActiveBroker } from '../../../hooks/useActiveBroker';
import { RISK_VIDEO_URL } from '../../../config/videos';

/**
 * A list item is a plain string, or an array of segments where { em } renders
 * inside <strong>. Keeps the emphasis already live on this page word-for-word
 * without putting markup in the content file.
 */
const renderSegments = (item) =>
  typeof item === 'string'
    ? item
    : item.map((seg, i) => (typeof seg === 'string' ? seg : <strong key={i}>{seg.em}</strong>));

const AutomatedTradingDisclaimerModal = ({ limitTotalTrades }) => {
  const { setShowDisclaimerModal, updateStocksFromForm, updateStockStatus, setShowAddModal, setShowRiskDisclosure, setShowAllSymbolsSelectedPopup } = useStockStore();
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { brokerName } = useActiveBroker();
  
  const handleAgree = async () => {
    if (isSubmitting) {
      console.log('⏳ Prevent duplicate Engage submit');
      return;
    }
    if (isChecked) {
      setIsSubmitting(true);
      console.log('✅ User agreed to terms, proceeding...');
      const actionId = `engage_${Date.now()}`;

      // Record the acknowledgment itself.
      //
      // The screen has always promised "Your acknowledgment will be time-stamped and
      // securely stored as part of your user agreement" (storageNote below), but
      // nothing was ever written: accepting recorded tbl_engagement and
      // tbl_engagement_logs — the TRADING engagement — and no consent row at all.
      // So there was no answer to "did this client agree, when, and to which wording".
      //
      // acceptedAt is serverTimestamp(), not a browser clock: this is the evidentiary
      // field and a client's device time is neither trustworthy nor, for anyone outside
      // ET, correct. version pins the exact wording shown (see config/riskDisclosure.js)
      // so later edits to the text cannot silently rewrite what was agreed to.
      //
      // Written before the engagement below on purpose — if consent cannot be recorded
      // we would rather know than have the trading engagement land without it. It does
      // NOT block the flow: a failure here is logged and the client proceeds, because
      // the acknowledgment on screen has already happened and refusing to engage at
      // this point would be a worse outcome than a consent row we have to backfill.
      const acknowledgingUid = auth?.currentUser?.uid;
      if (acknowledgingUid && db) {
        try {
          await setDoc(
            doc(db, 'users', acknowledgingUid),
            {
              riskDisclosure: {
                version: DISCLOSURE_VERSION,
                acceptedAt: serverTimestamp(),
                documents: ['risk-disclosure', 'trading-warnings'],
                acknowledgedVideoWatched: true,
              },
            },
            { merge: true },
          );
          console.log(`✅ Risk disclosure acknowledgment recorded (version ${DISCLOSURE_VERSION})`);
        } catch (ackError) {
          console.error('🚨 Failed to record risk disclosure acknowledgment:', ackError?.message);
        }
      } else {
        console.warn('⚠️ No authenticated user — risk disclosure acknowledgment not recorded');
      }
      
      // Get the temporary stocks data that was stored in window.tempStocksData
      const stocksToUpdate = window.tempStocksData || [];
      console.log('🔍 Stocks to update:', stocksToUpdate);
      console.log('🔍 window.tempStocksData:', window.tempStocksData);
      
      // If no stocks data, try to get from AddWatchlistModal or create dummy data
      if (!stocksToUpdate || stocksToUpdate.length === 0) {
        console.log('⚠️ No stocks data found, creating dummy data for testing...');
        
        // Create dummy data for testing
        const dummyStocks = [
          {
            symbol: 'SPX',
            value: 500,
            type: 'spread',
            spread: 2,
            hedge: false,
            status: 'Pending',
            quanlity: 500,
            broker: 'tradier'
          }
        ];
        
        console.log('🔍 Using dummy stocks:', dummyStocks);
        
        // Process dummy stocks
        try {
          for (const stock of dummyStocks) {
            try {
              // Get current user's account info
              const currentUser = auth.currentUser;
              const accountName = currentUser?.displayName || currentUser?.email || 'Unknown User';
              const broker = stock.broker || 'tradier';
              
              console.log('🔍 Processing dummy stock:', { symbol: stock.symbol, type: stock.type, quantity: stock.quanlity });
              
              // Automation logs service removed - no longer storing data
              console.log('⏭️ Skipping automation logs storage for', stock.symbol);
              
              // Log to tbl_notifications for notifications
              const logMessage = `Engage:\n${stock.symbol} ${stock.quanlity} ${stock.type === 'spread' ? 'Spreads' : 'Single Leg(s)'}\nLimit total number of trades : ${limitTotalTrades}`;
              
              await setDoc(doc(db, 'tbl_notifications', `${(currentUser?.uid || 'anonymous')}_${stock.symbol}_${actionId}`), {
                log: logMessage,
                timestamp: serverTimestamp(),
                userId: currentUser?.uid || 'anonymous',
                symbol: stock.symbol,
                status: 'Engage',
                action: 'Engage',
                stockStatus: 'Active',
                stockType: stock.type,
                stockQuanlity: stock.quanlity,
                stockValue: stock.quanlity,
                accountName: accountName,
                type: 'stock_engagement',
                actionId
              });
              console.log('✅ Dummy engage logged to tbl_notifications:', logMessage);
            } catch (error) {
              console.error('❌ Error processing dummy stock', stock.symbol, ':', error);
            }
          }
          
          console.log('✅ All dummy stocks processed successfully');
        } catch (error) {
          console.error('❌ Error processing dummy stocks:', error);
        }
        
        // Close all modals and popups
        setShowDisclaimerModal(false);
        setShowAddModal(false);
        setShowRiskDisclosure(false);
        setShowAllSymbolsSelectedPopup(false);
        console.log('✅ All modals closed (dummy stocks processed)');
        setIsSubmitting(false);
        return;
      }
      
      // Update the stocks with the new data
      if (stocksToUpdate.length > 0) {
        try {
          // Update stocks in store
          updateStocksFromForm(stocksToUpdate);
          stocksToUpdate.forEach((s) =>
            updateStockStatus(s.symbol, 'Pending', s.hedge)
          );

          // Log to tbl_engagement for notifications only
          for (const stock of stocksToUpdate) {
            try {
              // Get current user's account info
              const currentUser = auth.currentUser;
              const accountName = currentUser?.displayName || currentUser?.email || 'Unknown User';
              
              console.log('🔍 Processing stock:', { symbol: stock.symbol, type: stock.type, quantity: stock.quanlity });
              
              // Log to tbl_engagement for notifications
              const logMessage = `Engage:\n${stock.symbol} ${stock.quanlity} ${stock.type === 'spread' ? 'Spreads' : 'Single Leg(s)'}\nLimit total number of trades : ${limitTotalTrades}`;
              
              await setDoc(doc(db, 'tbl_engagement', `${accountName}_${stock.symbol}`), {
                username: accountName,
                userId: currentUser?.uid || 'anonymous',
                broker: brokerName || 'Unknown',
                symbol: stock.symbol,
                status: 'active',
                type: stock.type || null,
                spread: stock.type === 'spread' ? (Number(stock.spread) || 2) : 1,
                maxPerTrade: stock.quanlity || 0,
                maxSimultaneous: limitTotalTrades,
                timestamp: serverTimestamp()
              }, { merge: true });
              console.log('✅ Engage logged to tbl_engagement:', logMessage);

              // Log to permanent engagement logs (tbl_engagement_logs) - optional, non-blocking
              // Fire-and-forget: Don't await to avoid blocking user experience
              if (currentUser?.uid && db) {
                addDoc(collection(db, 'tbl_engagement_logs'), {
                  userId: currentUser.uid,
                  symbol: stock.symbol,
                  timestamp: serverTimestamp(),
                  action: 'order_created',
                  operation: 'create',
                  status: 'active',
                  correlationId: `engage_${Date.now()}_${currentUser.uid}_${stock.symbol}`,
                  metadata: {
                    quantity: stock.quanlity,
                    orderSide: 'engage',
                    brokerType: brokerName || 'Unknown',
                    type: stock.type,
                    spread: stock.type === 'spread' ? (Number(stock.spread) || 2) : 1,
                    reason: 'User engaged symbol from frontend'
                  },
                  createdAt: serverTimestamp()
                }).then(() => {
                  console.log('✅ Engagement logged to tbl_engagement_logs (permanent)');
                }).catch(logError => {
                  console.warn('⚠️ Failed to log to tbl_engagement_logs (non-critical):', logError?.message || logError);
                  // Don't block the main flow if logging fails
                });
              }

              // Also log to tbl_notifications (idempotent)
              await setDoc(doc(db, 'tbl_notifications', `${(currentUser?.uid || 'anonymous')}_${stock.symbol}_${actionId}`), {
                log: logMessage,
                timestamp: serverTimestamp(),
                userId: currentUser?.uid || 'anonymous',
                symbol: stock.symbol,
                status: 'Engage',
                action: 'Engage',
                stockStatus: 'Active',
                stockType: stock.type,
                stockQuanlity: stock.quanlity,
                stockValue: stock.quanlity,
                accountName: accountName,
                type: 'stock_engagement',
                actionId
              });
              console.log('✅ Engage also logged to tbl_notifications:', logMessage);
            } catch (error) {
              console.error('❌ Error processing stock', stock.symbol, ':', error);
            }
          }

          delete window.tempStocksData;
          console.log('✅ All stocks processed successfully');
        } catch (error) {
          console.error('❌ Error updating stocks:', error);
        }
      } else {
        console.log('⚠️ No stocks to update');
      }
      
      // Close all modals and popups
      setShowDisclaimerModal(false);
      setShowAddModal(false);
      setShowRiskDisclosure(false);
      setShowAllSymbolsSelectedPopup(false);
      console.log('✅ All modals closed');
    } else {
      console.log('⚠️ User must check the checkbox to continue');
      alert('You must agree to the terms to continue.');
    }
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    setShowDisclaimerModal(false);
    setShowAddModal(false);
    delete window.tempStocksData;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center px-4">
      <div className="bg-gray-900 text-white w-full max-w-lg rounded-xl p-2 md:p-4 shadow-xl border border-purple-600 flex flex-col max-h-[90vh] overflow-hidden">
        <h2 className="text-xl font-bold mb-2 text-center">Final Disclaimer & Acknowledgment of Risk</h2>

        {/* Disclaimer Video */}
        <div className="mb-2 flex justify-center">
          <video className="max-w-full" controls>
            <source src={RISK_VIDEO_URL} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        {/* WARNING Message */}
        <div className="overflow-y-auto max-h-32 bg-gray-800 p-2 rounded-lg text-xs leading-relaxed space-y-2 mb-2">
          <p><strong>WARNING:</strong> You should not use this system unless you fully understand and accept these risks.</p>
        </div>

        {/* Disclaimer Content */}
        <div className="overflow-y-auto max-h-64 bg-gray-800 p-2 rounded-lg text-xs leading-relaxed space-y-2">
          {TRADING_WARNINGS.sections.map((section, idx) => (
            <React.Fragment key={section.heading}>
              {/* Spacing preserved verbatim: the first three headings carry no mt-4
                  today and the rest do. Normalising it would be a visual change
                  nobody asked for, so the quirk stays here rather than leaking
                  into the content file. */}
              <h3 className={idx < 3 ? 'font-semibold' : 'font-semibold mt-4'}>{section.heading}</h3>
              <p>{section.lead}</p>
              <ul className="list-disc ml-6">
                {section.items.map((item, i) => (
                  <li key={i}>{renderSegments(item)}</li>
                ))}
              </ul>
            </React.Fragment>
          ))}

          <div className="flex items-center space-x-2 mt-3">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => setIsChecked(!isChecked)}
              id="disclaimer-checkbox"
              className="h-4 w-4"
            />
            <label htmlFor="disclaimer-checkbox" className="text-xs">
              {TRADING_WARNINGS.checkboxLabel}
            </label>
          </div>
          <p className="mt-2 text-xs">{TRADING_WARNINGS.storageNote}</p>
        </div>

        {/* Buttons */}
        <div className="flex justify-end mt-3 space-x-2">
          <button onClick={handleCancel} className="px-3 py-1.5 bg-gray-700 rounded text-xs hover:bg-gray-600">
            I do not agree
          </button>
          <button
            onClick={handleAgree}
            className={`px-3 py-1.5 bg-purple-600 rounded text-xs hover:bg-purple-500 ${(!isChecked || isSubmitting) ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!isChecked || isSubmitting}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutomatedTradingDisclaimerModal;
